<script>
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import CompanyModel from '@models/Company';
import isFunction from 'lodash/isFunction';
import Contacts from '@views/companies/contacts';
import { quillEditor } from 'vue-quill-editor';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';

export default {
  components: {
    BlockingContainer,
    quillEditor,
    Contacts,
  },
  mixins: [editFormChange],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    isModal: {
      default: false,
      type: Boolean,
    }
  },
  data() {
    return {
      company: {
        name: null,
        additional_name: null,
        street: null,
        postal_code: null,
        town: null,
        country: null,
        debitor_number: null,
        vat_number: null,
        notes: null,
        invoicing_details: null,
      },
      editorOptions: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'], // toggled buttons
            ['blockquote', 'code-block'],

            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
            [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }], // dropdown with defaults from theme
            [{ font: [] }],
            [{ align: [] }],
            ['clean'],
          ],
        },
      },
    };
  },
  mounted() {
    if (this.loadedId) {
      this.$wait.start('company loading');
      CompanyModel.find(this.loadedId).then((response) => {
        this.company = Object.assign({}, this.company, response.company);
        this.$wait.end('company loading');
        this.$nextTick(() => {
          this.initChangeWatcher(['company'], this.validate);
        });
      });
    }
  },
  methods: {
    validate(callback) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$wait.start('company loading');
          this.submit(callback);
        }
      });
    },
    submit(callback) {
      if (!this.isModal && this.$route.params.id) {
        this.company.id = this.$route.params.id;
      }

      new CompanyModel(this.company)
        .save()
        .then((response) => {
          this.$emit('submitted', response.company);
          if (callback && isFunction(callback)) callback();
          this.$wait.end('company loading');
        })
        .catch(() => this.$wait.end('company loading'));
    },
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('company loading')"></BlockingContainer>
    <b-form @submit.prevent="validate">
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.name') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          id="name"
          v-model="company.name"
          v-validate="'required|max:255'"
          data-vv-value-path="localValue"
          data-vv-name="name"
          :data-vv-as="$t('fields.name')"
          type="text"
          :state="errors.has('name') ? false : null"
          aria-describedby="input-feedback-name"
          :placeholder="$t('placeholders.name')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-name">
          <template v-show="errors.has('name')">{{ errors.first('name') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.additional_name') }}:
        </template>
        <b-form-input
          id="additional_name"
          v-model="company.additional_name"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="additional_name"
          :data-vv-as="$t('fields.additional_name')"
          type="text"
          :state="errors.has('additional_name') ? false : null"
          aria-describedby="input-feedback-additional_name"
          :placeholder="$t('placeholders.additional_name')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-additional_name">
          <template v-show="errors.has('additional_name')">{{ errors.first('additional_name') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>      
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.street') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          id="street"
          v-model="company.street"
          v-validate="'required|max:255'"
          data-vv-value-path="localValue"
          data-vv-name="street"
          :data-vv-as="$t('fields.street')"
          type="text"
          :state="errors.has('street') ? false : null"
          aria-describedby="input-feedback-street"
          :placeholder="$t('placeholders.street')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-street">
          <template v-show="errors.has('street')">{{ errors.first('street') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.postal_code') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          id="postal_code"
          v-model="company.postal_code"
          v-validate="'required|max:255'"
          data-vv-value-path="localValue"
          data-vv-name="postal_code"
          :data-vv-as="$t('fields.postal_code')"
          type="text"
          :state="errors.has('postal_code') ? false : null"
          aria-describedby="input-feedback-postal_code"
          :placeholder="$t('placeholders.postal_code')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-postal_code">
          <template v-show="errors.has('postal_code')">{{ errors.first('postal_code') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.town') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          id="town"
          v-model="company.town"
          v-validate="'required|max:255'"
          data-vv-value-path="localValue"
          data-vv-name="town"
          :data-vv-as="$t('fields.town')"
          type="text"
          :state="errors.has('town') ? false : null"
          aria-describedby="input-feedback-town"
          :placeholder="$t('placeholders.town')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-town">
          <template v-show="errors.has('town')">{{ errors.first('town') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.country') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          id="country"
          v-model="company.country"
          v-validate="'required|max:255'"
          data-vv-value-path="localValue"
          data-vv-name="country"
          :data-vv-as="$t('fields.country')"
          type="text"
          :state="errors.has('country') ? false : null"
          aria-describedby="input-feedback-country"
          :placeholder="$t('placeholders.country')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-country">
          <template v-show="errors.has('country')">{{ errors.first('country') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.invoicing_details') }}:
        </template>
        <b-form-input
          id="invoicing_details"
          v-model="company.invoicing_details"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="invoicing_details"
          :data-vv-as="$t('fields.invoicing_details')"
          type="text"
          :state="errors.has('invoicing_details') ? false : null"
          aria-describedby="input-feedback-invoicing_details"
          :placeholder="$t('placeholders.enter_details')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-invoicing_details">
          <template v-show="errors.has('invoicing_details')">{{ errors.first('invoicing_details') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.vat_number')}:`">
        <b-form-input
          id="vat_number"
          v-model="company.vat_number"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="vat_number"
          :data-vv-as="$t('fields.vat_number')"
          type="text"
          :state="errors.has('vat_number') ? false : null"
          aria-describedby="input-feedback-vat_number"
          :placeholder="$t('placeholders.vat_number')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-vat_number">
          <template v-show="errors.has('vat_number')">{{ errors.first('vat_number') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.debitor_number')}:`">
        <b-form-input
          id="debitor_number"
          v-model="company.debitor_number"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="debitor_number"
          :data-vv-as="$t('fields.debitor_number')"
          type="text"
          :state="errors.has('debitor_number') ? false : null"
          aria-describedby="input-feedback-debitor_number"
          :placeholder="$t('placeholders.debitor_number')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-debitor_number">
          <template v-show="errors.has('debitor_number')">{{ errors.first('debitor_number') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.notes')}:`">
        <quill-editor ref="notes" v-model="company.notes" :options="editorOptions" />
      </b-form-group>
      <b-button type="submit" :disabled="errors.any()" :variant="'outline-' + (!errors.any() ? 'dark' : 'danger')">
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
  <Contacts v-if="company.contacts" :data="company.contacts" class="mt-2" />
  </div>
</template>

<style lang="scss" module>
@import '@design';

.avatarImg {
  height: 160px;
  &:hover {
    cursor: pointer;
    border: 1px solid map_get($theme-colors, 'dark');
  }
}

.clearButtonContainer {
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 0 !important;
}
</style>
